import { pdfjs } from "react-pdf";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "react-query";
import { DashboardLayout } from "components/layout/components/DashboardLayout";
import { Navbar } from "components/layout/components/Navbar";
import {
  Input,
  Select,
  Button,
  Form,
  DatePicker,
  Radio,
  Upload,
  Spin,
  message,
} from "antd";
import { HomeOutlined, RightOutlined, RobotOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { routePath } from "config/routeMgt/routePath";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import dayjs from "dayjs";
import { openNotification } from "utils/notification";
import { generalValidationRules } from "utils/formHelpers/validations";
import {
  LEVEL_LIST,
  WORK_MODELS,
  COUNTRIES,
  EMPLOYMENT_TYPES,
  LANGUAGES,
} from "constants/general";
import { useJobFormStore } from "../hooks/useJobFormStore";
import { useGetSingleJob } from "../hooks/useGetSingleJob";
import WarningModal from "components/layout/components/WarningModal";
import {
  useGetCities,
  useGetSectors,
  useGetJobTitles,
  prefetchAppData,
} from "../hooks/useCacheData";
import { AppButton } from "components/button/AppButton";
import axios from "axios";
import { END_POINT } from "config/environment";
import * as mammoth from "mammoth";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useExtractInformationJD } from "../hooks/useExtractInformationJD";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

/** Stable Quill config — a new object each render re-inits the editor and can freeze the page. */
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, false] }],
    [{ bold: true }, { italic: true }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ link: true }],
    ["clean"],
  ],
};

interface ParsedJobData {
  title: string;
  company_name: string;
  location_country: string;
  location_city: string;
  level: string;
  type: string;
  employment_type: string;
  experience: string;
  skills: Array<{ name: string }>;
  language: string;
  sector: string;
}

const fixTextFormatting = (text: string): string => {
  if (!text) return "";

  let fixed = text;
  fixed = fixed.replace(/(\w+)-\s*\n\s*(\w+)/g, "$1-$2");
  fixed = fixed.replace(/(\w+)-\s*\r\s*(\w+)/g, "$1-$2");
  fixed = fixed.replace(/(\w+)-\s*<\/p>\s*<p>\s*(\w+)/g, "$1-$2");
  fixed = fixed.replace(/(\w+)-\s*\n\s*\t\s*(\w+)/g, "$1-$2");
  fixed = fixed.replace(/(\w+)-\s*\t\s*\n\s*(\w+)/g, "$1-$2");
  fixed = fixed.replace(/(\w+)-\s*\n\s*-\s*(\w+)/g, "$1-$2");
  fixed = fixed.replace(/(\w+)\s*-\s*(\w+)/g, "$1-$2");

  fixed = fixed.replace(/([•·▪])\s*([A-Za-z])/g, (match, bullet, text) => {
    return `\n${bullet} ${text}`;
  });

  fixed = fixed.replace(/[◦○]\s*/g, "◦ ");
  fixed = fixed.replace(/^\s*◦\s+/gm, "  ◦ ");
  fixed = fixed.replace(/\n\s*◦\s+/g, "\n  ◦ ");

  fixed = fixed.replace(/\n{3,}/g, "\n\n");

  return fixed;
};

const cleanAndFormatDescription = (html: string): string => {
  if (!html) return "";

  let cleaned = html;

  cleaned = cleaned.replace(/style="[^"]*"/gi, "");
  cleaned = cleaned.replace(/<div[^>]*>/gi, "");
  cleaned = cleaned.replace(/<\/div>/gi, "");
  cleaned = cleaned.replace(
    /<p><strong>Job Position:<\/strong>[^<]*<\/p>/gi,
    "",
  );
  cleaned = cleaned.replace(
    /<p><strong>Reporting Relationship:<\/strong>[^<]*<\/p>/gi,
    "",
  );

  cleaned = fixTextFormatting(cleaned);

  cleaned = cleaned.replace(/\s{2,}/g, " ");
  cleaned = cleaned.replace(/\n{3,}/g, "\n\n");

  return cleaned;
};

export const cleanAndFormatJobDescription = (text: string): string => {
  if (!text) return "";

  const jobSummaryIndex = text.indexOf("Job Summary");
  if (jobSummaryIndex !== -1) {
    text = text.substring(jobSummaryIndex);
  }

  let plainText = text.replace(/<(.|\n)*?>/g, "").trim();

  const sentences = plainText.split(/\.\s+/).filter((s) => s.trim().length > 0);
  const uniqueSentences: string[] = [];
  const seen = new Set();

  for (const sentence of sentences) {
    const normalized = sentence.trim().toLowerCase().substring(0, 30);
    if (!seen.has(normalized)) {
      seen.add(normalized);
      uniqueSentences.push(sentence.trim());
    }
  }

  let cleaned = uniqueSentences.join(". ");
  if (cleaned && !cleaned.endsWith(".")) {
    cleaned += ".";
  }

  cleaned = cleaned
    .replace(/:\s*/g, ": ")
    .replace(/\s{2,}/g, " ")
    .replace(/\.([A-Z])/g, ". $1")
    .replace(/Key\s*Performance\s*Indicators/gi, "Key Performance Indicators")
    .replace(/Minimum\s*qualifications/i, "Minimum Qualifications");

  const sectionHeaders = [
    "Job Summary",
    "Principal Duties and Responsibilities",
    "Competencies",
    "Skills",
    "Attributes",
    "Knowledge",
    "Key Performance Indicators",
    "Minimum Qualifications",
    "Other Requirements",
    "Education",
    "Experience",
    "Language",
    "Supervised By",
    "Grade Level",
    "Division",
    "Job Position",
  ];

  let formattedText = cleaned;

  for (const header of sectionHeaders) {
    const regex = new RegExp(`(${header})`, "gi");
    formattedText = formattedText.replace(regex, `\n\n$1`);
  }

  formattedText = formattedText.replace(
    /Competencies\s*\([^)]*\)/gi,
    (match) => `\n\n${match}`,
  );

  const sections = formattedText.split(/\n\n+/);

  const formattedSections = sections.map((section) => {
    const trimmedSection = section.trim();
    if (!trimmedSection) return "";

    const isHeader = sectionHeaders.some(
      (header) =>
        trimmedSection.toLowerCase().startsWith(header.toLowerCase()) ||
        trimmedSection.toLowerCase().includes(header.toLowerCase()),
    );

    const isCompetenciesHeader = /competencies\s*\([^)]*\)/i.test(
      trimmedSection,
    );

    if (isHeader || isCompetenciesHeader) {
      return `<h4 class="font-semibold mt-4 mb-2 text-gray-800">${trimmedSection}</h4>`;
    }

    const lines = trimmedSection.split(/\n/);
    const hasBullets = lines.some(
      (line) =>
        /^[•·o]\s/.test(line.trim()) ||
        /^-\s[A-Z]/.test(line.trim()) ||
        /^\d+\.\s/.test(line.trim()),
    );

    if (hasBullets) {
      const listItems = lines
        .filter((line) => line.trim().length > 0)
        .map((line) => {
          let cleanLine = line
            .replace(/^[•·o]\s/, "")
            .replace(/^-\s[A-Z]/, "")
            .replace(/^\d+\.\s/, "")
            .trim();
          cleanLine = cleanLine.replace(/\s{2,}/g, " ");
          return `<li class="ml-6 mb-1 text-gray-700">${cleanLine}</li>`;
        });
      return `<ul class="list-disc mb-3">${listItems.join("")}</ul>`;
    }

    const sentenceParts = trimmedSection.split(/(?<=\.\s)(?=[A-Z])/);
    const paragraphs: string[] = [];
    let currentParagraph: string[] = [];

    for (const part of sentenceParts) {
      if (currentParagraph.length >= 4) {
        paragraphs.push(currentParagraph.join(""));
        currentParagraph = [];
      }
      currentParagraph.push(part.trim());
    }

    if (currentParagraph.length > 0) {
      paragraphs.push(currentParagraph.join(""));
    }

    return paragraphs
      .map((p) => `<p class="mb-3 text-gray-700 leading-relaxed">${p}</p>`)
      .join("");
  });

  let result = formattedSections.filter((s) => s).join("\n");

  result = result
    .replace(
      /Key Performance IndicatorsMinimum/g,
      "Key Performance Indicators\n\nMinimum",
    )
    .replace(
      /Minimum Qualifications & Other Requirements/g,
      "Minimum Qualifications\n\nOther Requirements",
    )
    .replace(/Competencies\s+Competencies/g, "Competencies")
    .replace(/<\/h4>\s*<p/g, "</h4>\n\n<p");

  return result;
};

const normalizeCountryName = (countryName: string): string | null => {
  if (!countryName) return null;

  const countryMap: Record<string, string> = {
    USA: "United States",
    US: "United States",
    "U.S.A": "United States",
    "U.S": "United States",
    UK: "United Kingdom",
    "U.K": "United Kingdom",
    UAE: "United Arab Emirates",
    Nigeria: "Nigeria",
    Canada: "Canada",
    Australia: "Australia",
    Germany: "Germany",
    France: "France",
    Kenya: "Kenya",
    "South Africa": "South Africa",
  };

  if (countryMap[countryName]) {
    return countryMap[countryName];
  }

  const exactMatch = COUNTRIES.find((c) => c.name === countryName);
  if (exactMatch) return exactMatch.name;

  const caseMatch = COUNTRIES.find(
    (c) => c.name.toLowerCase() === countryName.toLowerCase(),
  );
  if (caseMatch) return caseMatch.name;

  const partialMatch = COUNTRIES.find(
    (c) =>
      c.name.toLowerCase().includes(countryName.toLowerCase()) ||
      countryName.toLowerCase().includes(c.name.toLowerCase()),
  );

  return partialMatch ? partialMatch.name : null;
};

const cleanTextHelper = (text: string): string => {
  if (!text) return "";
  return text
    .trim()
    .replace(/^[:\s\-]+/, "")
    .replace(/[^\w\s\/&()-]/g, "")
    .substring(0, 100);
};

const parseJobDescription = (text: string): ParsedJobData => {
  const result: ParsedJobData = {
    title: "",
    company_name: "",
    location_country: "",
    location_city: "",
    level: "",
    type: "",
    employment_type: "",
    experience: "",
    skills: [],
    language: "English",
    sector: "",
  };

  if (!text || text.trim().length === 0) return result;

  const cleanText = text.replace(/\s+/g, " ").trim();
  const lowerText = cleanText.toLowerCase();
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);

  for (let i = 0; i < Math.min(15, lines.length); i++) {
    const line = lines[i].trim();
    if (!line) continue;

    let match = line.match(/Job\s*Title:?\s*(.+)/i);
    if (match) {
      result.title = cleanTextHelper(match[1]);
      break;
    }
    match = line.match(/Position:?\s*(.+)/i);
    if (match) {
      result.title = cleanTextHelper(match[1]);
      break;
    }
    match = line.match(/Role:?\s*(.+)/i);
    if (match) {
      result.title = cleanTextHelper(match[1]);
      break;
    }
    match = line.match(/Hiring:?\s*(.+)/i);
    if (match) {
      result.title = cleanTextHelper(match[1]);
      break;
    }

    if (!result.title && line.length < 100 && line.length > 3) {
      const looksLikeTitle =
        /^[A-Z][a-zA-Z0-9\s\/&\(\)\-]+$/.test(line) &&
        !line.toLowerCase().includes("company") &&
        !line.toLowerCase().includes("location") &&
        !line.toLowerCase().includes("description");
      if (looksLikeTitle) {
        result.title = line.trim();
      }
    }
  }

  if (!result.title) {
    const firstParagraph = lines.slice(0, 5).join(" ");
    const capsMatch = firstParagraph.match(
      /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,4})\b/,
    );
    if (capsMatch && !capsMatch[1].toLowerCase().includes("inc")) {
      result.title = capsMatch[1];
    }
  }

  const companyPatterns = [
    /Company:?\s*([^\n]{2,80})/i,
    /Company\s*Name:?\s*([^\n]{2,80})/i,
    /Organization:?\s*([^\n]{2,80})/i,
    /Employer:?\s*([^\n]{2,80})/i,
    /@\s*([A-Z][a-zA-Z\s&]+)(?:\s+is\s+hiring|\s+is\s+looking|\s+seeks|\n|$)/i,
    /(?:at|with|for)\s+([A-Z][a-zA-Z\s&\.]+)(?:\s+\(|\s+is\s+|\s+are\s+|\s+has\s+|\s+we\s+|\n|$)/i,
    /([A-Z][a-zA-Z\s&]+)\s+(?:is hiring|is seeking|is looking|are hiring)/i,
  ];

  for (const pattern of companyPatterns) {
    const match = cleanText.match(pattern);
    if (match && match[1] && match[1].length < 80 && match[1].length > 2) {
      let company = match[1].trim();
      company = company.replace(/^(the|a|an)\s+/i, "");
      if (
        !company.toLowerCase().includes("position") &&
        !company.toLowerCase().includes("location") &&
        !company.toLowerCase().includes("salary")
      ) {
        result.company_name = company;
        break;
      }
    }
  }

  const countryList = COUNTRIES.map((c) => c.name.toLowerCase());

  const explicitLocation = cleanText.match(
    /Location:?\s*([^,\n]+(?:,\s*[^,\n]+)?)/i,
  );
  if (explicitLocation) {
    const location = explicitLocation[1].trim();
    for (const country of countryList) {
      if (location.toLowerCase().includes(country)) {
        result.location_country =
          country.charAt(0).toUpperCase() + country.slice(1);
        const cityMatch = location.match(/([^,]+),/i);
        if (cityMatch) {
          result.location_city = cityMatch[1].trim();
        }
        break;
      }
    }
    if (!result.location_country) {
      result.location_country = location;
    }
  }

  if (!result.location_country) {
    for (const country of countryList) {
      if (lowerText.includes(country)) {
        result.location_country =
          country.charAt(0).toUpperCase() + country.slice(1);

        const countryIndex = lowerText.indexOf(country);
        const beforeCountry = cleanText.substring(
          Math.max(0, countryIndex - 50),
          countryIndex,
        );
        const cityMatch = beforeCountry.match(/([A-Z][a-z]+)(?:\s*,)?\s*$/);
        if (cityMatch && cityMatch[1].length < 30) {
          result.location_city = cityMatch[1];
        }
        break;
      }
    }
  }

  if (result.location_country) {
    const normalized = normalizeCountryName(result.location_country);
    if (normalized) result.location_country = normalized;
  }

  const levelKeywords: Record<string, string[]> = {
    "Entry Level": [
      "entry level",
      "entry-level",
      "junior",
      "graduate",
      "associate",
      "0-2",
      "0-3",
      "1-2",
      "0-2 years",
      "entry",
    ],
    "Mid Level": [
      "mid level",
      "mid-level",
      "intermediate",
      "3-5",
      "3-6",
      "4-6",
      "5-7",
      "3-5 years",
    ],
    "Senior Level": [
      "senior",
      "sr.",
      "principal",
      "6-8",
      "7-10",
      "8-10",
      "10+",
      "senior level",
    ],
    "Lead Level": [
      "lead",
      "manager",
      "director",
      "head of",
      "leadership",
      "team lead",
      "technical lead",
    ],
  };

  for (const [level, keywords] of Object.entries(levelKeywords)) {
    if (keywords.some((k) => lowerText.includes(k))) {
      result.level = level;
      break;
    }
  }

  if (/\bremote\b/i.test(cleanText) && !/\bnot remote\b/i.test(cleanText)) {
    result.type = "Remote";
  } else if (/\bhybrid\b/i.test(cleanText)) {
    result.type = "Hybrid";
  } else if (/\bonsite\b|\bon-site\b/i.test(cleanText)) {
    result.type = "Onsite";
  }

  if (!result.type) {
    if (lowerText.includes("work from home") || lowerText.includes("wfh")) {
      result.type = "Remote";
    } else if (lowerText.includes("office based")) {
      result.type = "Onsite";
    }
  }

  if (/\bfull[- ]time\b|\bfulltime\b/i.test(cleanText)) {
    result.employment_type = "Full-Time";
  } else if (/\bpart[- ]time\b|\bparttime\b/i.test(cleanText)) {
    result.employment_type = "Part-Time";
  } else if (/\bcontract\b|\bfixed[- ]term\b/i.test(cleanText)) {
    result.employment_type = "Contract";
  } else if (/\binternship\b|\bintern\b/i.test(cleanText)) {
    result.employment_type = "Internship";
  } else if (/\bfreelance\b/i.test(cleanText)) {
    result.employment_type = "Contract";
  }

  const expPatterns = [
    /(\d+)\s*[-–—]\s*(\d+)\s*(?:years?|yrs?)/i,
    /(\d+)\+?\s*years?\s+(?:of\s+)?experience/i,
    /experience:\s*(\d+)\s*[-–—]\s*(\d+)/i,
    /(\d+)\s*[-–—]\s*(\d+)\s*years?/i,
    /minimum\s+(\d+)\s*years?/i,
    /at\s+least\s+(\d+)\s*years?/i,
    /(\d+)[\+]?\s*yrs?/i,
  ];

  for (const pattern of expPatterns) {
    const match = cleanText.match(pattern);
    if (match) {
      if (match[2] && parseInt(match[2]) > 0) {
        result.experience = `${match[1]}-${match[2]}`;
      } else if (match[1]) {
        const years = parseInt(match[1]);
        if (years > 0 && years < 30) {
          result.experience = `${years}+ years`;
        }
      }
      break;
    }
  }

  const skillsSectionPatterns = [
    /(?:key|required|desired|preferred)\s+skills:?\s*([^\.]{1,800})/i,
    /(?:qualifications|requirements|technical\s+skills):?\s*([^\.]{1,800})/i,
    /(?:competencies|what\s+you'll\s+need):?\s*([^\.]{1,800})/i,
    /(?:you\s+have|we\s+require):?\s*([^\.]{1,800})/i,
  ];

  let skillsText = "";
  for (const pattern of skillsSectionPatterns) {
    const match = cleanText.match(pattern);
    if (match) {
      skillsText = match[1];
      break;
    }
  }

  if (!skillsText) {
    const reqMatch = cleanText.match(/requirements?:?\s*([^\.]{1,500})/i);
    if (reqMatch) skillsText = reqMatch[1];
  }

  const skillKeywords = [
    "python",
    "javascript",
    "typescript",
    "java",
    "react",
    "angular",
    "vue",
    "node.js",
    "express",
    "django",
    "sql",
    "mongodb",
    "aws",
    "docker",
    "kubernetes",
    "agile",
    "scrum",
    "leadership",
    "communication",
    "teamwork",
  ];

  if (skillsText) {
    const lowerSkillsText = skillsText.toLowerCase();
    const foundSkills: string[] = [];

    for (const skill of skillKeywords) {
      if (
        lowerSkillsText.includes(skill.toLowerCase()) &&
        !foundSkills.includes(skill)
      ) {
        foundSkills.push(skill);
        if (foundSkills.length >= 8) break;
      }
    }

    result.skills = foundSkills.map((skill) => ({
      name: skill
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" "),
    }));
  }

  const languageList = [
    "English",
    "French",
    "Spanish",
    "German",
    "Chinese",
    "Japanese",
    "Arabic",
    "Portuguese",
    "Russian",
    "Dutch",
  ];
  for (const lang of languageList) {
    const langPattern = new RegExp(
      `\\b${lang}\\b|${lang}\\s*(?:language|speaking|fluent)`,
      "i",
    );
    if (langPattern.test(cleanText)) {
      result.language = lang;
      break;
    }
  }

  const sectors = [
    "Technology",
    "Finance",
    "Healthcare",
    "Education",
    "Retail",
    "Manufacturing",
    "Construction",
    "Real Estate",
    "Transportation",
    "Hospitality",
    "Media",
    "Telecommunications",
    "Energy",
    "Government",
    "Nonprofit",
    "Consulting",
  ];

  for (const sector of sectors) {
    if (lowerText.includes(sector.toLowerCase())) {
      result.sector = sector;
      break;
    }
  }

  return result;
};

const parseJobDescriptionWithAI = async (
  text: string,
): Promise<ParsedJobData> => {
  try {
    const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;

    if (!API_KEY) {
      console.error("Gemini API key is missing");
      throw new Error("API key not configured");
    }

    const countryNames = COUNTRIES.map((c) => c.name).join(", ");

    const prompt = `
      You are a job description parser. Extract the following information from this job description. 
      Return ONLY valid JSON, no other text, no markdown formatting, no explanations.
      
      Job Description:
      ${text.substring(0, 6000)}
      
      Extract these fields exactly as a JSON object:
      {
        "title": "the exact job title/position name",
        "company_name": "the company name hiring for this role",
        "location_country": "must be exactly one of: ${countryNames}",
        "location_city": "the city where the job is located",
        "level": "must be exactly one of: Entry Level, Mid Level, Senior Level, Lead Level",
        "type": "must be exactly one of: Remote, Hybrid, Onsite",
        "employment_type": "must be exactly one of: Full-Time, Part-Time, Contract, Internship",
        "experience": "years of experience required as a string like '3-5 years' or '5+ years'",
        "skills": ["skill1", "skill2", "skill3"],
        "language": "the primary language required",
        "sector": "the industry sector"
      }
      
      If a field is not found in the job description, use an empty string for strings and an empty array for skills.
      Only output the JSON object, nothing else.
    `;

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1/models/gemini-pro:generateContent?key=${API_KEY}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: prompt,
                },
              ],
            },
          ],
          generationConfig: {
            temperature: 0.1,
            topK: 1,
            topP: 1,
            maxOutputTokens: 1024,
          },
        }),
      },
    );

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();

    if (
      !data.candidates ||
      !data.candidates[0] ||
      !data.candidates[0].content
    ) {
      throw new Error("Invalid API response");
    }

    let jsonText = data.candidates[0].content.parts[0].text;

    jsonText = jsonText.trim();
    jsonText = jsonText.replace(/```json\s*/g, "");
    jsonText = jsonText.replace(/```\s*/g, "");

    const jsonMatch = jsonText.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      jsonText = jsonMatch[0];
    }

    const parsed = JSON.parse(jsonText);

    if (parsed.location_country) {
      const normalized = normalizeCountryName(parsed.location_country);
      if (normalized) parsed.location_country = normalized;
    }

    return {
      title: parsed.title || "",
      company_name: parsed.company_name || "",
      location_country: parsed.location_country || "",
      location_city: parsed.location_city || "",
      level: parsed.level || "",
      type: parsed.type || "",
      employment_type: parsed.employment_type || "",
      experience: parsed.experience || "",
      skills: (parsed.skills || []).map((s: string) => ({ name: s })),
      language: parsed.language || "English",
      sector: parsed.sector || "",
    };
  } catch (error) {
    throw error;
  }
};

export const JobsCreation = () => {
  const [form] = Form.useForm();
  const { token, userInfo } = useGetUserInfo();
  const queryClient = useQueryClient();
  const { mutateAsync: extractJD } = useExtractInformationJD();
  const hydratedListingKeyRef = useRef<string | null>(null);
  const isResettingRef = useRef(false);
  const resetTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isCancelModalVisible, setCancelModalVisible] = useState(false);
  const {
    title,
    company_name,
    sector_id,
    location_country,
    location_city,
    level,
    type,
    employment_type,
    experience,
    language,
    skills,
    expiryDate,
    description,
    descriptionInputType,
    descriptionFile,
    updateStep1,
    updateStep2,
    setIsEditing,
    setJobId,
    resetForm,
    loadFromAPI,
  } = useJobFormStore();

  const params = useParams();
  const rawParamId = params.id as string | undefined;
  const isEdit = rawParamId?.includes("___edit") ?? false;
  const isReopen = rawParamId?.includes("___reopen") ?? false;

  let id = rawParamId as number | string | undefined;
  if (isEdit) {
    id = rawParamId!.replace("___edit", "");
  } else if (isReopen) {
    id = rawParamId!.replace("___reopen", "");
  }

  const { data: jobData, isSuccess } = useGetSingleJob({
    token,
    id: id as number,
  });

  useEffect(() => {
    if (!isReopen || !rawParamId) return;
    setJobId(undefined);
    setIsEditing(false);
  }, [isReopen, rawParamId, setJobId, setIsEditing]);

  const { data: sectorData, isLoading: loadingSector } = useGetSectors(token);
  const { data: jobTitlesData, isLoading: loadingJobTitles } =
    useGetJobTitles(token);
  const [selectedJobTitleId, setSelectedJobTitleId] = useState<number | null>(
    null,
  );

  const [selectedCountry, setSelectedCountry] = useState<string | null>(
    location_country,
  );
  const [languageState, setLanguageState] = useState<string | null>(language);
  const [companyName, setCompanyName] = useState<string | null>(company_name);

  const [editorValue, setEditorValue] = useState<string>(description || "");
  const [file, setFile] = useState<any>(descriptionFile || null);
  const [localDescriptionInputType, setLocalDescriptionInputType] = useState<
    "upload" | "manual" | null
  >(descriptionInputType || null);

  const [manualQuillKey, setManualQuillKey] = useState(0);
  const [descriptionExceedsManualLimit, setDescriptionExceedsManualLimit] =
    useState(false);
  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [autoFillCount, setAutoFillCount] = useState(0);
  const [uploadKey, setUploadKey] = useState(0);

  const maxChars = 4000;
  const cleanValue = (editorValue || "").replace(/<(.|\n)*?>/g, "").trim();
  const charCount = cleanValue.length;
  const isOverLimit = charCount > maxChars;

  // Keep typing local-only. Syncing Zustand (persist → localStorage) on every
  // keystroke re-renders this large form and freezes the page in production.
  const handleEditorChange = useCallback((val: string) => {
    setEditorValue(val);
  }, []);

  const { data: citiesData, isLoading: loadingCities } = useGetCities(
    selectedCountry || undefined,
    token,
  );

  const jobTitleOptions = React.useMemo(() => {
    if (!jobTitlesData) return [];
    return [
      ...jobTitlesData.map((jobTitle: { name: any }) => ({
        label: jobTitle.name,
        value: jobTitle.name,
      })),
      { label: "Others", value: "others" },
    ];
  }, [jobTitlesData]);

  const sectorOptions = React.useMemo(() => {
    if (!sectorData) return [];
    return sectorData.map((sector: { id: any; name: any }) => ({
      value: sector.id,
      label: sector.name,
    }));
  }, [sectorData]);

  const cityOptions = React.useMemo(() => {
    if (!citiesData) return [];
    return citiesData.map((city: { name: any }) => ({
      value: city.name,
      label: city.name,
    }));
  }, [citiesData]);

  const getJobTitleId = (titleName: string) => {
    const jobTitle = jobTitlesData?.find(
      (jt: { name: string }) =>
        jt.name.toLowerCase() === titleName.toLowerCase(),
    );
    return jobTitle?.id || null;
  };

  const clearStoredData = () => {
    localStorage.removeItem("jobCreationFormData");
  };

  const saveFormDataToLocalStorage = () => {
    if (isEdit || isReopen || id) return;

    const formData = {
      title,
      company_name,
      sector_id,
      location_country,
      location_city,
      level,
      type,
      employment_type,
      experience,
      language,
      expiryDate: expiryDate ? dayjs(expiryDate).toISOString() : null,
      description: editorValue,
      descriptionInputType: localDescriptionInputType,
      skills,
      editorValue: editorValue,
      selectedCountry,
      languageState,
      selectedJobTitleId,
      fileName: file?.name || null,
      fileSize: file?.size || null,
      fileType: file?.type || null,
      fileUid: file?.uid || null,
      fileUploaded: !!file,
      savedAt: Date.now(),
    };
    localStorage.setItem("jobCreationFormData", JSON.stringify(formData));
  };

  const restoreFormDataFromLocalStorage = () => {
    const savedData = localStorage.getItem("jobCreationFormData");
    if (!savedData) return false;

    try {
      const parsed = JSON.parse(savedData);

      if (
        parsed.savedAt &&
        Date.now() - parsed.savedAt > 7 * 24 * 60 * 60 * 1000
      ) {
        localStorage.removeItem("jobCreationFormData");
        return false;
      }

      if (isEdit || isReopen || id) {
        return false;
      }

      const currentSkills = useJobFormStore.getState().skills;
      const hasExistingSkills = currentSkills && currentSkills.length > 0;

      if (hasExistingSkills) {
        if (parsed.title) updateStep1({ title: parsed.title });
        if (parsed.company_name)
          updateStep1({ company_name: parsed.company_name });
        if (parsed.sector_id) updateStep1({ sector_id: parsed.sector_id });
        if (parsed.location_country) {
          updateStep1({ location_country: parsed.location_country });
          setSelectedCountry(parsed.location_country);
        }
        if (parsed.location_city)
          updateStep1({ location_city: parsed.location_city });
        if (parsed.level) updateStep1({ level: parsed.level });
        if (parsed.type) updateStep1({ type: parsed.type });
        if (parsed.employment_type)
          updateStep1({ employment_type: parsed.employment_type });
        if (parsed.experience) updateStep1({ experience: parsed.experience });
        if (parsed.language) {
          updateStep1({ language: parsed.language });
          setLanguageState(parsed.language);
        }
        if (parsed.expiryDate)
          updateStep1({ expiryDate: dayjs(parsed.expiryDate) });
        if (parsed.description) {
          updateStep2({ description: parsed.description });
          setEditorValue(parsed.description);
        }
        if (parsed.descriptionInputType)
          setLocalDescriptionInputType(parsed.descriptionInputType);
        if (parsed.selectedJobTitleId)
          setSelectedJobTitleId(parsed.selectedJobTitleId);

        const formValues: any = {
          company_name: parsed.company_name,
          sector_id: parsed.sector_id,
          location_country: parsed.location_country,
          location_city: parsed.location_city,
          level: parsed.level,
          type: parsed.type,
          employment_type: parsed.employment_type,
          experience: parsed.experience,
          language: parsed.language,
          expiryDate: parsed.expiryDate ? dayjs(parsed.expiryDate) : null,
          descriptionMethod: parsed.descriptionInputType,
        };

        if (parsed.title) {
          const titleExists = jobTitleOptions.some(
            (job: { value: string }) =>
              job.value.toLowerCase() === parsed.title.toLowerCase(),
          );
          if (titleExists) {
            formValues.title = parsed.title;
          } else {
            formValues.title = "others";
            formValues.customTitle = parsed.title;
          }
        }

        form.setFieldsValue(formValues);

        if (parsed.fileUploaded && parsed.fileName) {
          const restoredFile = {
            uid: parsed.fileUid || Date.now(),
            name: parsed.fileName,
            size: parsed.fileSize,
            type: parsed.fileType || "application/octet-stream",
            status: "done",
          };
          setFile(restoredFile);
          updateStep2({ descriptionFile: restoredFile });
        }

        if (parsed.descriptionInputType === "manual" && parsed.editorValue) {
          setEditorValue(parsed.editorValue);
          setManualQuillKey((k) => k + 1);
          form.setFieldsValue({ manualDescription: parsed.editorValue });
          updateStep2({ description: parsed.editorValue });
        }

        return true;
      }

      if (parsed.title) updateStep1({ title: parsed.title });
      if (parsed.company_name)
        updateStep1({ company_name: parsed.company_name });
      if (parsed.sector_id) updateStep1({ sector_id: parsed.sector_id });
      if (parsed.location_country) {
        updateStep1({ location_country: parsed.location_country });
        setSelectedCountry(parsed.location_country);
      }
      if (parsed.location_city)
        updateStep1({ location_city: parsed.location_city });
      if (parsed.level) updateStep1({ level: parsed.level });
      if (parsed.type) updateStep1({ type: parsed.type });
      if (parsed.employment_type)
        updateStep1({ employment_type: parsed.employment_type });
      if (parsed.experience) updateStep1({ experience: parsed.experience });
      if (parsed.language) {
        updateStep1({ language: parsed.language });
        setLanguageState(parsed.language);
      }
      if (parsed.expiryDate)
        updateStep1({ expiryDate: dayjs(parsed.expiryDate) });
      if (parsed.description) {
        updateStep2({ description: parsed.description });
        setEditorValue(parsed.description);
      }
      if (parsed.descriptionInputType)
        setLocalDescriptionInputType(parsed.descriptionInputType);
      if (parsed.selectedJobTitleId)
        setSelectedJobTitleId(parsed.selectedJobTitleId);

      // Only restore skills if there are NO existing skills
      if (parsed.skills && parsed.skills.length > 0) {
        updateStep2({ skills: parsed.skills });
      }

      const formValues: any = {
        company_name: parsed.company_name,
        sector_id: parsed.sector_id,
        location_country: parsed.location_country,
        location_city: parsed.location_city,
        level: parsed.level,
        type: parsed.type,
        employment_type: parsed.employment_type,
        experience: parsed.experience,
        language: parsed.language,
        expiryDate: parsed.expiryDate ? dayjs(parsed.expiryDate) : null,
        descriptionMethod: parsed.descriptionInputType,
      };

      if (parsed.title) {
        const titleExists = jobTitleOptions.some(
          (job: { value: string }) =>
            job.value.toLowerCase() === parsed.title.toLowerCase(),
        );
        if (titleExists) {
          formValues.title = parsed.title;
        } else {
          formValues.title = "others";
          formValues.customTitle = parsed.title;
        }
      }

      form.setFieldsValue(formValues);

      if (parsed.fileUploaded && parsed.fileName) {
        const restoredFile = {
          uid: parsed.fileUid || Date.now(),
          name: parsed.fileName,
          size: parsed.fileSize,
          type: parsed.fileType || "application/octet-stream",
          status: "done",
        };
        setFile(restoredFile);
        updateStep2({ descriptionFile: restoredFile });
      }

      if (parsed.descriptionInputType === "manual" && parsed.editorValue) {
        setEditorValue(parsed.editorValue);
        setManualQuillKey((k) => k + 1);
        form.setFieldsValue({ manualDescription: parsed.editorValue });
        updateStep2({ description: parsed.editorValue });
      }

      return true;
    } catch (error) {
      localStorage.removeItem("jobCreationFormData");
      return false;
    }
  };

  const autoFillForm = (parsedData: ParsedJobData) => {
    const formUpdates: any = {};

    if (parsedData.title && !title) {
      const existingJobTitle = jobTitleOptions.find(
        (job: { value: string }) =>
          job.value.toLowerCase() === parsedData.title.toLowerCase(),
      );

      if (existingJobTitle) {
        formUpdates.title = parsedData.title;
        updateStep1({ title: parsedData.title });
        const jobTitleIdValue = getJobTitleId(parsedData.title);
        if (jobTitleIdValue) {
          setSelectedJobTitleId(jobTitleIdValue);
          updateStep1({ jobTitleId: jobTitleIdValue });
        }
      } else if (parsedData.title.length > 2) {
        formUpdates.title = "others";
        formUpdates.customTitle = parsedData.title;
        updateStep1({ title: parsedData.title });
      }
    }

    if (parsedData.location_country && !location_country) {
      const normalizedCountry = normalizeCountryName(
        parsedData.location_country,
      );
      if (normalizedCountry) {
        formUpdates.location_country = normalizedCountry;
        updateStep1({ location_country: normalizedCountry });
        setSelectedCountry(normalizedCountry);

        if (parsedData.location_city) {
          setTimeout(() => {
            form.setFieldsValue({ location_city: parsedData.location_city });
            updateStep1({ location_city: parsedData.location_city });
          }, 1500);
        }
      }
    }

    if (parsedData.level && !level) {
      formUpdates.level = parsedData.level;
      updateStep1({ level: parsedData.level });
    }

    if (parsedData.type && !type) {
      formUpdates.type = parsedData.type;
      updateStep1({ type: parsedData.type });
    }

    if (parsedData.employment_type && !employment_type) {
      formUpdates.employment_type = parsedData.employment_type;
      updateStep1({ employment_type: parsedData.employment_type });
    }

    if (parsedData.experience && !experience) {
      formUpdates.experience = parsedData.experience;
      updateStep1({ experience: parsedData.experience });
    }

    if (parsedData.language && !language) {
      formUpdates.language = parsedData.language;
      updateStep1({ language: parsedData.language });
      setLanguageState(parsedData.language);
    }

    if (parsedData.company_name && !company_name) {
      formUpdates.company_name = parsedData.company_name;
      updateStep1({ company_name: parsedData.company_name });
      setCompanyName(parsedData.company_name);
    }

    if (parsedData.sector && !sector_id) {
      const matchedSector = sectorOptions.find(
        (option: { label: string; value: number }) =>
          option.label.toLowerCase() === parsedData.sector.toLowerCase(),
      );

      if (matchedSector) {
        formUpdates.sector_id = matchedSector.value;
        updateStep1({ sector_id: matchedSector.value });
      }
    }

    if (parsedData.skills && parsedData.skills.length > 0) {
      updateStep2({ skills: parsedData.skills });
    }

    if (Object.keys(formUpdates).length > 0) {
      form.setFieldsValue(formUpdates);
    }
  };

  const handleFileChange = async (info: any) => {
    if (info.file.status === "done") {
      setIsAutoFilling(true);
      setAutoFillCount(0);

      const fileReader = new FileReader();

      fileReader.onload = async () => {
        const arrayBuffer = fileReader.result as ArrayBuffer;
        const fileType = info.file.type;

        try {
          let plainText = "";
          let htmlContent = "";

          if (fileType === "application/pdf") {
            const loadingTask = pdfjs.getDocument(new Uint8Array(arrayBuffer));
            const pdf = await loadingTask.promise;

            let fullText = "";

            for (let i = 1; i <= pdf.numPages; i++) {
              const page = await pdf.getPage(i);
              const textContent = await page.getTextContent();

              const lines: { [y: number]: string[] } = {};

              textContent.items.forEach((item: any) => {
                const y = Math.round(item.transform[5]);
                if (!lines[y]) {
                  lines[y] = [];
                }
                lines[y].push(item.str);
              });

              const sortedY = Object.keys(lines).sort(
                (a, b) => parseInt(b) - parseInt(a),
              );

              let pageText = "";
              sortedY.forEach((y, index) => {
                const lineText = lines[parseInt(y)].join(" ").trim();
                if (lineText) {
                  pageText += lineText + "\n";
                }
              });

              fullText += pageText + "\n\n";
              plainText += pageText + "\n";
              htmlContent += `<p>${pageText}</p>`;
            }

            plainText = fullText;
          } else if (
            fileType ===
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          ) {
            const result = await mammoth.convertToHtml({ arrayBuffer });
            htmlContent = result.value;
            const rawTextResult = await mammoth.extractRawText({ arrayBuffer });
            plainText = rawTextResult.value;
          } else if (fileType === "text/plain") {
            plainText = new TextDecoder().decode(arrayBuffer);
            htmlContent = plainText.replace(/\n/g, "<br>");
          } else {
            throw new Error(`Unsupported file type: ${fileType}`);
          }

          let processedHtml = "";

          const lines = plainText.split("\n").filter((line) => line.trim());

          let inList = false;
          let result = [];
          let currentSection = "";
          let isBulletLine = false;

          for (let i = 0; i < lines.length; i++) {
            let line = lines[i].trim();

            if (!line) continue;

            const bulletMatch = line.match(/^[•·▪◦○●◆■\-]\s+(.+)/);
            const bulletNumberMatch = line.match(/^(\d+)\.\s+(.+)/);
            const startsWithBullet = /^[•·▪◦○●◆■\-]/.test(line);

            const isSectionHeader =
              /^[A-Z][a-z]+(\s+[A-Z][a-z]+)*$/.test(line) &&
              line.length < 50 &&
              !startsWithBullet &&
              !line.match(/^[a-z]/);

            const isSubHeader =
              /^(Finance and General Operations|Treasury and Investment Operations|Investee Company Support|Account Management|Internal|External)/i.test(
                line,
              );

            if (bulletMatch || bulletNumberMatch || startsWithBullet) {
              let content = "";
              if (bulletMatch) {
                content = bulletMatch[1];
              } else if (bulletNumberMatch) {
                content = bulletNumberMatch[2];
              } else {
                content = line.replace(/^[•·▪◦○●◆■\-]\s*/, "");
              }

              if (!inList) {
                result.push('<ul class="list-disc mb-3">');
                inList = true;
              }

              result.push(
                `<li class="ml-6 mb-1 text-gray-700">${content}</li>`,
              );
              isBulletLine = true;
            } else {
              if (inList) {
                result.push("</ul>");
                inList = false;
              }

              if (
                isSectionHeader ||
                isSubHeader ||
                /^(Job Summary|Principal Duties and Responsibilities|Competencies|Skills|Attributes|Knowledge|Key Performance Indicators|Minimum Qualifications|Other Requirements|Education|Experience|Language|Supervised By|Grade Level|Division|Job Position)/i.test(
                  line,
                )
              ) {
                result.push(
                  `<h4 class="font-semibold mt-4 mb-2 text-gray-800">${line}</h4>`,
                );
              } else {
                result.push(
                  `<p class="mb-3 text-gray-700 leading-relaxed">${line}</p>`,
                );
              }
              isBulletLine = false;
            }
          }

          if (inList) {
            result.push("</ul>");
          }

          processedHtml = result.join("\n");

          setEditorValue(processedHtml);
          updateStep2({ description: processedHtml });

          setFile(info.file);
          updateStep2({ descriptionFile: info.file });

          form.setFields([
            {
              name: "descriptionFile",
              errors: [],
              value: info.file,
            },
          ]);

          if (plainText.trim()) {
            let parsedData: ParsedJobData;
            let extractionSuccess = false;

            try {
              const apiResponse = await extractJD({
                jd: plainText,
                token,
              });

              let mappedLevel = apiResponse.level || "";
              if (mappedLevel === "executive") {
                mappedLevel = "senior_level";
              }
              parsedData = {
                title: apiResponse.jobTitle || "",
                company_name: apiResponse.company_name || "",
                location_country: apiResponse.location_country || "",
                location_city: apiResponse.location_city || "",
                level: mappedLevel,
                type: apiResponse.type || "",
                employment_type: apiResponse.employment_type || "",
                experience: apiResponse.years_of_experience || "",
                skills: (apiResponse.skills || []).map((skill: string) => ({
                  name: skill,
                })),
                language: apiResponse.language || "English",
                sector: apiResponse.sector || "",
              };

              if (apiResponse.description) {
                updateStep2({ description: apiResponse.description });
                setEditorValue(apiResponse.description);
              }

              extractionSuccess = true;

              message.success({
                content: "Job description extracted successfully!",
                duration: 4,
              });
            } catch (error: any) {
              let errorMessage =
                "We're having trouble processing your document right now. Please try again later or paste the text manually.";

              if (error?.response?.status && error.response.status >= 500) {
                errorMessage =
                  "Our AI service is currently unavailable. Please try again in a few minutes or paste the text manually.";
              }
              else if (
                error?.code === "ECONNABORTED" ||
                error?.message?.includes("timeout")
              ) {
                errorMessage =
                  "The request is taking too long. Please try again or paste the text manually.";
              }
              else if (error?.message?.includes("Network Error")) {
                errorMessage =
                  "Network connection issue. Please check your internet and try again.";
              }
              else if (
                error?.response?.status === 401 ||
                error?.response?.status === 403
              ) {
                errorMessage =
                  "Authentication failed. Please refresh the page and try again.";
              }
              else if (error?.response?.status === 429) {
                errorMessage =
                  "Too many requests. Please wait a moment and try again.";
              }
              else if (
                error?.response?.status &&
                error.response.status >= 400 &&
                error.response.status < 500
              ) {
                errorMessage =
                  "There was an issue with the document format. Please try pasting the text manually.";
              }

              console.error("API JD extraction failed:", error);

              message.error({
                content: errorMessage,
                duration: 6,
              });

              parsedData = parseJobDescription(plainText);

              if (parsedData) {
                updateStep2({ description: processedHtml });
                setEditorValue(processedHtml);
              }
            }

            if (parsedData) {
              autoFillForm(parsedData);
            }
          } else {
            message.warning({
              content:
                "Could not extract text from the document. Please ensure the file is not empty or corrupted.",
              duration: 5,
            });
          }
        } catch (error) {
          message.error({
            content:
              "Failed to process the file. Please try a different file or paste the text manually.",
            duration: 6,
          });
        } finally {
          setIsAutoFilling(false);
        }
      };

      fileReader.onerror = () => {
        message.error({
          content: "Failed to read the file. Please try again.",
          duration: 4,
        });
        setIsAutoFilling(false);
      };

      fileReader.readAsArrayBuffer(info.file.originFileObj);
    }
  };

  const handleExtractManualText = async () => {
    const plainText = editorValue.replace(/<(.|\n)*?>/g, "").trim();

    if (!plainText || plainText.length === 0) {
      message.warning({
        content: "Please paste some text to extract.",
        duration: 3,
      });
      return;
    }

    setIsAutoFilling(true);

    try {
      const apiResponse = await extractJD({
        jd: plainText,
        token,
      });

      if (apiResponse) {
        const formUpdates: any = {};

        if (apiResponse.jobTitle) {
          const placeholderPatterns = [
            "[Insert Job Title]",
            "[Insert Job Title Here]",
            "[Job Title]",
            "Insert Job Title",
            "Job Title Here",
            "[Insert Position]",
            "Insert Position",
            "[Insert]",
            "Insert",
          ];

          const isPlaceholder = placeholderPatterns.some((pattern) =>
            apiResponse.jobTitle?.toLowerCase().includes(pattern.toLowerCase()),
          );

          if (!isPlaceholder && apiResponse.jobTitle.length > 2) {
            const existingJobTitle = jobTitleOptions.find(
              (job: { value: string }) =>
                job.value.toLowerCase() === apiResponse.jobTitle?.toLowerCase(),
            );

            if (existingJobTitle) {
              formUpdates.title = apiResponse.jobTitle;
              updateStep1({ title: apiResponse.jobTitle });
              const jobTitleIdValue = getJobTitleId(apiResponse.jobTitle);
              if (jobTitleIdValue) {
                setSelectedJobTitleId(jobTitleIdValue);
                updateStep1({ jobTitleId: jobTitleIdValue });
              }
            } else {
              formUpdates.title = "others";
              formUpdates.customTitle = apiResponse.jobTitle;
              updateStep1({ title: apiResponse.jobTitle });
            }
          }
        }

        if (apiResponse.company_name) {
          const companyPlaceholders = [
            "[Insert Company Name]",
            "Company Name",
            "[Company Name]",
          ];
          const isCompanyPlaceholder = companyPlaceholders.some((pattern) =>
            apiResponse.company_name
              ?.toLowerCase()
              .includes(pattern.toLowerCase()),
          );

          if (!isCompanyPlaceholder && apiResponse.company_name.length > 2) {
            formUpdates.company_name = apiResponse.company_name;
            updateStep1({ company_name: apiResponse.company_name });
            setCompanyName(apiResponse.company_name);
          }
        }

        // Location Country
        if (apiResponse.location_country) {
          const countryPlaceholders = [
            "[Insert Location]",
            "Location",
            "[Location]",
          ];
          const isCountryPlaceholder = countryPlaceholders.some((pattern) =>
            apiResponse.location_country
              ?.toLowerCase()
              .includes(pattern.toLowerCase()),
          );

          if (!isCountryPlaceholder) {
            const normalizedCountry = normalizeCountryName(
              apiResponse.location_country,
            );
            if (normalizedCountry) {
              formUpdates.location_country = normalizedCountry;
              updateStep1({ location_country: normalizedCountry });
              setSelectedCountry(normalizedCountry);

              if (apiResponse.location_city) {
                const cityPlaceholders = ["[Insert City]", "City", "[City]"];
                const isCityPlaceholder = cityPlaceholders.some((pattern) =>
                  apiResponse.location_city
                    ?.toLowerCase()
                    .includes(pattern.toLowerCase()),
                );

                if (!isCityPlaceholder) {
                  setTimeout(() => {
                    form.setFieldsValue({
                      location_city: apiResponse.location_city,
                    });
                    updateStep1({
                      location_city: apiResponse.location_city || "",
                    });
                  }, 500);
                }
              }
            }
          }
        }

        if (apiResponse.level) {
          let mappedLevel = apiResponse.level;
          if (mappedLevel === "executive") {
            mappedLevel = "Senior Level";
          } else if (mappedLevel === "mid") {
            mappedLevel = "Mid Level";
          } else if (mappedLevel === "entry") {
            mappedLevel = "Entry Level";
          } else if (mappedLevel === "senior") {
            mappedLevel = "Senior Level";
          } else if (mappedLevel === "mid_level") {
            mappedLevel = "Mid Level";
          }
          formUpdates.level = mappedLevel;
          updateStep1({ level: mappedLevel });
        }

        if (apiResponse.type) {
          const typePlaceholders = [
            "[Insert Work Model]",
            "Work Model",
            "Onsite",
          ];
          const isTypePlaceholder = typePlaceholders.some((pattern) =>
            apiResponse.type?.toLowerCase().includes(pattern.toLowerCase()),
          );

          if (!isTypePlaceholder) {
            formUpdates.type = apiResponse.type;
            updateStep1({ type: apiResponse.type });
          }
        }

        if (apiResponse.employment_type) {
          const employmentPlaceholders = [
            "[Insert Employment Type]",
            "Full-Time",
            "Part-Time",
            "Contract",
          ];
          const isEmploymentPlaceholder = employmentPlaceholders.some(
            (pattern) =>
              apiResponse.employment_type
                ?.toLowerCase()
                .includes(pattern.toLowerCase()),
          );

          if (!isEmploymentPlaceholder) {
            formUpdates.employment_type = apiResponse.employment_type;
            updateStep1({ employment_type: apiResponse.employment_type });
          }
        }

        if (apiResponse.years_of_experience) {
          const expPlaceholders = ["[Insert Years]", "0", "0 years", "[Years]"];
          const isExpPlaceholder = expPlaceholders.some((pattern) =>
            String(apiResponse.years_of_experience)
              ?.toLowerCase()
              .includes(pattern.toLowerCase()),
          );

          if (!isExpPlaceholder && apiResponse.years_of_experience !== "0") {
            formUpdates.experience = apiResponse.years_of_experience;
            updateStep1({ experience: apiResponse.years_of_experience });
          }
        }

        // Language
        if (apiResponse.language) {
          const langPlaceholders = ["[Insert Language]", "Language", "English"];
          const isLangPlaceholder = langPlaceholders.some((pattern) =>
            apiResponse.language?.toLowerCase().includes(pattern.toLowerCase()),
          );

          if (!isLangPlaceholder) {
            formUpdates.language = apiResponse.language;
            updateStep1({ language: apiResponse.language });
            setLanguageState(apiResponse.language);
          }
        }

        if (apiResponse.sector) {
          const sectorPlaceholders = [
            "[Insert Sector]",
            "Sector",
            "Healthcare",
          ];
          const isSectorPlaceholder = sectorPlaceholders.some((pattern) =>
            apiResponse.sector?.toLowerCase().includes(pattern.toLowerCase()),
          );

          if (!isSectorPlaceholder) {
            const matchedSector = sectorOptions.find(
              (option: { label: string; value: number }) =>
                option.label.toLowerCase() ===
                apiResponse.sector?.toLowerCase(),
            );
            if (matchedSector) {
              formUpdates.sector_id = matchedSector.value;
              updateStep1({ sector_id: matchedSector.value });
            }
          }
        }

        if (apiResponse.skills && apiResponse.skills.length > 0) {
          const skillPlaceholders = [
            "[Insert Skill]",
            "Insert Skill",
            "Skill",
            "Skills",
            "Strong clinical knowledge",
            "Excellent communication",
            "Ability to work under pressure",
            "Proficiency in using medical software",
            "Compassionate and patient-focused approach",
          ];

          const filteredSkills = apiResponse.skills.filter((skill: string) => {
            const isPlaceholder = skillPlaceholders.some((placeholder) =>
              skill.toLowerCase().includes(placeholder.toLowerCase()),
            );
            return !isPlaceholder && skill.length > 2;
          });

          if (filteredSkills.length > 0) {
            const formattedSkills = filteredSkills.map((skill: string) => ({
              name: skill,
            }));
            updateStep2({ skills: formattedSkills });
          }
        }

        if (apiResponse.description) {
          let formattedDesc = cleanAndFormatDescription(
            apiResponse.description,
          );
          formattedDesc = fixTextFormatting(formattedDesc);

          setEditorValue(formattedDesc);
          updateStep2({ description: formattedDesc });
          form.setFieldsValue({ manualDescription: formattedDesc });
        }

        if (Object.keys(formUpdates).length > 0) {
          form.setFieldsValue(formUpdates);
          message.success({
            content: "Job description extracted successfully!",
            duration: 4,
          });
        } else {
          message.info({
            content: "No new data could be extracted from the text.",
            duration: 3,
          });
        }
      }
    } catch (error: any) {
      console.error("Error extracting text:", error);

      let errorMessage =
        "We're having trouble processing your text right now. Please try again later.";

      if (error?.response?.status) {
        const status = error.response.status;

        if (status >= 500) {
          errorMessage =
            "Our AI service is currently unavailable. Please try again in a few minutes or paste the text manually.";
        } else if (status === 429) {
          errorMessage =
            "Too many requests. Please wait a moment and try again.";
        } else if (status === 401 || status === 403) {
          errorMessage =
            "Authentication failed. Please refresh the page and try again.";
        } else if (status >= 400 && status < 500) {
          errorMessage =
            "There was an issue with the document format. Please try pasting the text manually.";
        }
      } else if (
        error?.code === "ECONNABORTED" ||
        error?.message?.includes("timeout")
      ) {
        errorMessage =
          "The request is taking too long. Please try again or paste the text manually.";
      } else if (error?.message?.includes("Network Error")) {
        errorMessage =
          "Network connection issue. Please check your internet and try again.";
      }

      message.error({
        content: errorMessage,
        duration: 6,
      });
    } finally {
      setIsAutoFilling(false);
    }
  };

  const handleJobTitleChange = (value: string) => {
    if (value !== "others") {
      const jobTitleId = getJobTitleId(value);
      setSelectedJobTitleId(jobTitleId);

      if (jobTitleId) {
        queryClient.prefetchQuery(
          ["skills-by-jobtitle", jobTitleId],
          () =>
            axios
              .get(`${END_POINT.BASE_URL}/v1/skills`, {
                params: { jobtitle_id: jobTitleId },
                headers: { Authorization: `Bearer ${token}` },
              })
              .then((res) => res.data),
          {
            staleTime: 1000 * 60 * 60 * 24,
          },
        );
      }

      form.setFieldsValue({ customTitle: undefined });
      updateStep1({
        title: value,
        jobTitleId: jobTitleId,
      });
    }
  };

  useEffect(() => {
    if (token) {
      prefetchAppData(queryClient, token).catch(console.error);
    }
  }, [token, queryClient]);

  useEffect(() => {
    if (!id) return;
    if (!isSuccess || !jobData) return;

    const listingId = (jobData as { id?: number | string }).id ?? "";
    const routeKey = String(rawParamId ?? "");
    const hydrateKey = `${routeKey}:${listingId}`;
    if (hydratedListingKeyRef.current === hydrateKey) return;

    const stored = useJobFormStore.getState();
    const returningToSavedEdit =
      isEdit &&
      stored.isEditing &&
      Boolean(stored.title) &&
      String(stored.jobId ?? "") === String(id ?? "");
    if (returningToSavedEdit) {
      hydratedListingKeyRef.current = hydrateKey;
      return;
    }

    hydratedListingKeyRef.current = hydrateKey;

    if (isReopen) {
      loadFromAPI(jobData, { asNewListing: true });
    } else {
      loadFromAPI(jobData);
      setIsEditing(true);
      setJobId(id as any);
    }
    const descText =
      (jobData as { description?: string; jobsummary?: string }).description ||
      (jobData as { jobsummary?: string }).jobsummary ||
      "";

    const cleanedDesc = cleanAndFormatJobDescription(descText);
    const plainLen = (cleanedDesc || "")
      .replace(/<(.|\n)*?>/g, "")
      .trim().length;
    const exceedsManualLimit = cleanedDesc.length > 0 && plainLen > maxChars;

    if (exceedsManualLimit) {
      setDescriptionExceedsManualLimit(true);
      setLocalDescriptionInputType("upload");
      setEditorValue("");
      const blob = new Blob([cleanedDesc], {
        type: "text/html;charset=utf-8",
      });
      setFile(
        new File([blob], "job-description.html", {
          type: "text/html",
        }),
      );
      updateStep2({
        description: cleanedDesc,
        descriptionInputType: "upload",
        descriptionFile: null,
      });
    } else {
      setDescriptionExceedsManualLimit(false);
      setEditorValue(cleanedDesc);
      setLocalDescriptionInputType(cleanedDesc ? "manual" : null);
      if (cleanedDesc) {
        setManualQuillKey((k) => k + 1);
      }
    }
  }, [
    id,
    rawParamId,
    isSuccess,
    jobData,
    isReopen,
    loadFromAPI,
    maxChars,
    setIsEditing,
    setJobId,
    updateStep2,
  ]);

  useEffect(() => {
    const formValues: any = {
      title: title,
      company_name: company_name,
      sector_id: sector_id,
      location_country: location_country,
      location_city: location_city,
      level: level,
      type: type,
      employment_type: employment_type,
      experience: experience,
      language: language,
      expiryDate: expiryDate ? dayjs(expiryDate) : null,
    };

    if (
      title &&
      !jobTitleOptions.some(
        (job: { value: string }) =>
          job.value.toLowerCase() === title.toLowerCase(),
      )
    ) {
      formValues.title = "others";
      formValues.customTitle = title;
    }

    form.setFieldsValue(formValues);

    if (location_country) {
      setSelectedCountry(location_country);
    }
    if (language) {
      setLanguageState(language);
    }
  }, [
    title,
    company_name,
    sector_id,
    location_country,
    location_city,
    level,
    type,
    employment_type,
    experience,
    language,
    expiryDate,
    form,
    jobTitleOptions,
  ]);

  const titleLocked = isReopen;

  const handleCountryChange = (value: string) => {
    if (!value) {
      setSelectedCountry(null);
      updateStep1({ location_country: undefined });
      form.setFieldsValue({ location_city: undefined });
      return;
    }

    setSelectedCountry(value);
    updateStep1({ location_country: value });
    form.setFieldsValue({ location_city: undefined });
    updateStep1({ location_city: undefined });
  };

  const handleLanguageChange = (value: string) => {
    setLanguageState(value);
    updateStep1({ language: value });
  };

  const disablePastDates = (current: dayjs.Dayjs) => {
    return current && current.isBefore(dayjs().endOf("day"));
  };

  const navigate = useNavigate();

  const handleSubmit = async (formData: any) => {
    if (localDescriptionInputType === "upload" && !file && !description) {
      form.setFields([
        {
          name: "descriptionFile",
          errors: ["Please upload a job description file"],
        },
      ]);
      return;
    }

    if (
      localDescriptionInputType === "manual" &&
      (!editorValue ||
        editorValue.replace(/<(.|\n)*?>/g, "").trim().length === 0)
    ) {
      form.setFields([
        {
          name: "manualDescription",
          errors: ["Please enter a job description"],
        },
      ]);
      return;
    }

    form.setFields([
      {
        name: "manualDescription",
        errors: [],
      },
    ]);

    const jobTitle =
      formData.title === "others" ? formData.customTitle : formData.title;
    const jobTitleId =
      formData.title === "others" ? null : getJobTitleId(formData.title);

    updateStep1({
      title: jobTitle,
      jobTitleId: jobTitleId,
      company_name: formData.company_name,
      sector_id: formData.sector_id,
      location_country: formData.location_country,
      location_city: formData.location_city,
      level: formData.level,
      type: formData.type,
      employment_type: formData.employment_type,
      experience: formData.experience,
      language: formData.language,
      expiryDate: formData.expiryDate,
    });

    const descriptionPayload =
      localDescriptionInputType === "upload"
        ? description || editorValue
        : editorValue;

    let finalDescription = descriptionPayload;

    const existingSkills = useJobFormStore.getState().skills;
    let extractedSkills: Array<{ name: string }> = existingSkills || [];

    if (extractedSkills.length === 0) {
      if (localDescriptionInputType === "manual" && descriptionPayload) {
        finalDescription = descriptionPayload;
        try {
          const plainText = descriptionPayload.replace(/<(.|\n)*?>/g, "").trim();
          const parsedData = parseJobDescription(plainText);
          if (parsedData.skills && parsedData.skills.length > 0) {
            extractedSkills = parsedData.skills;
          }
        } catch (e) {
        }
      } else if (localDescriptionInputType === "upload") {
        finalDescription = descriptionPayload;
        try {
          const parsedData = parseJobDescription(
            descriptionPayload.replace(/<(.|\n)*?>/g, "").trim(),
          );
          if (parsedData.skills && parsedData.skills.length > 0) {
            extractedSkills = parsedData.skills;
          }
        } catch (e) {
          // Ignore - if parsing fails, keep empty skills
        }
      }
    }

    updateStep2({
      description: finalDescription,
      descriptionInputType: localDescriptionInputType,
      descriptionFile: file,
      skills: extractedSkills,
    });

    const saveFormData = () => {
      let serializableFile = null;
      if (file && file.originFileObj) {
        serializableFile = {
          name: file.name,
          size: file.size,
          type: file.type,
          uid: file.uid,
          lastModified: file.originFileObj.lastModified,
        };
      } else if (file) {
        serializableFile = {
          name: file.name,
          size: file.size,
          type: file.type,
          uid: file.uid,
        };
      }

      const dataToSave = {
        title: jobTitle,
        company_name: formData.company_name,
        sector_id: formData.sector_id,
        location_country: formData.location_country,
        location_city: formData.location_city,
        level: formData.level,
        type: formData.type,
        employment_type: formData.employment_type,
        experience: formData.experience,
        language: formData.language,
        expiryDate: formData.expiryDate,
        description: finalDescription,
        descriptionInputType: localDescriptionInputType,
        editorValue: finalDescription,
        selectedCountry: formData.location_country,
        languageState: formData.language,
        selectedJobTitleId: jobTitleId,
        file: serializableFile,
        fileUploaded: !!file,
        skills: extractedSkills,
      };
      sessionStorage.setItem("jobCreationFormData", JSON.stringify(dataToSave));
    };

    saveFormData();
    navigate(routePath.jobsCreation2, {
      state: {
        returnTo: rawParamId ? `/jobs-add/${rawParamId}` : "/jobs-add",
      },
    });
  };

  const formatDescriptionManually = (html: string): string => {
    if (!html) return "";

    if (html.includes("<ul") && html.includes("<li")) {
      return html;
    }

    let text = html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/\s{2,}/g, " ")
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const result: string[] = [];
    let inBulletList = false;

    for (let i = 0; i < text.length; i++) {
      let line = text[i];

      const bulletMatch = line.match(/^([■•·◦○\-])\s+(.+)/);
      const startsWithBullet = /^[■•·\-]\s/.test(line);
      const startsWithSubBullet = /^[○◦]\s/.test(line);

      if (bulletMatch) {
        const bulletChar = bulletMatch[1];
        const content = bulletMatch[2];

        if (!inBulletList) {
          result.push('<ul class="list-disc mb-3">');
          inBulletList = true;
        }
        result.push(
          `<li class="ml-6 mb-1 text-gray-700">${bulletChar} ${content}</li>`,
        );
      } else if (startsWithBullet) {
        const bulletChar = line.match(/^([■•·\-])/)?.[1] || "•";
        const content = line.replace(/^[■•·\-]\s*/, "").trim();

        if (!inBulletList) {
          result.push('<ul class="list-disc mb-3">');
          inBulletList = true;
        }
        result.push(
          `<li class="ml-6 mb-1 text-gray-700">${bulletChar} ${content}</li>`,
        );
      } else if (startsWithSubBullet) {
        const bulletChar = line.match(/^([○◦])/)?.[1] || "◦";
        const content = line.replace(/^[○◦]\s*/, "").trim();

        if (!inBulletList) {
          result.push('<ul class="list-disc mb-3">');
          inBulletList = true;
        }
        result.push(
          `<li class="ml-6 mb-1 text-gray-700">${bulletChar} ${content}</li>`,
        );
      } else {
        if (inBulletList) {
          result.push("</ul>");
          inBulletList = false;
        }

        const isHeader =
          /^(Job Summary|Principal Duties and Responsibilities|Competencies|Skills|Attributes|Knowledge|Key Performance Indicators|Minimum Qualifications|Other Requirements|Education|Experience|Language|Supervised By|Grade Level|Division|Job Position)/i.test(
            line,
          );

        if (isHeader) {
          result.push(
            `<h4 class="font-semibold mt-4 mb-2 text-gray-800">${line}</h4>`,
          );
        } else if (line) {
          result.push(
            `<p class="mb-3 text-gray-700 leading-relaxed">${line}</p>`,
          );
        }
      }
    }

    if (inBulletList) {
      result.push("</ul>");
    }

    return result.join("");
  };

  const handleCancel = () => {
    if (id) {
      navigate(-1);
    } else {
      setCancelModalVisible(true);
    }
  };

  const handleConfirmCancel = () => {
    clearStoredData();
    resetForm();
    navigate(routePath.jobs);
  };

  useEffect(() => {
    const pid = params.id;
    const isEditRoute = typeof pid === "string" && pid.includes("___edit");
    const isReopenRoute = typeof pid === "string" && pid.includes("___reopen");

    if (isEditRoute || isReopenRoute) {
      return;
    }

    hydratedListingKeyRef.current = null;

    resetForm();

    setEditorValue("");
    setFile(null);
    setLocalDescriptionInputType(null);
    setManualQuillKey(0);
    setDescriptionExceedsManualLimit(false);
    setAutoFillCount(0);
    setSelectedCountry(null);
    setLanguageState(null);
    setSelectedJobTitleId(null);

    form.resetFields();
  }, [params.id, resetForm]);

  useEffect(() => {
    if (isEdit || isReopen || id) return;

    const saveTimeout = setTimeout(() => {
      if (title || company_name || editorValue || file || skills?.length) {
        saveFormDataToLocalStorage();
      }
    }, 1000);

    return () => clearTimeout(saveTimeout);
  }, [
    title,
    company_name,
    sector_id,
    location_country,
    location_city,
    level,
    type,
    employment_type,
    experience,
    language,
    expiryDate,
    editorValue,
    localDescriptionInputType,
    selectedCountry,
    languageState,
    selectedJobTitleId,
    file,
    skills,
    isEdit,
    isReopen,
    id,
  ]);

  useEffect(() => {
    const currentSkills = useJobFormStore.getState().skills;

    if (
      !isEdit &&
      !isReopen &&
      !id &&
      (!currentSkills || currentSkills.length === 0)
    ) {
      const restored = restoreFormDataFromLocalStorage();
      if (restored) {
        const savedData = localStorage.getItem("jobCreationFormData");
        if (savedData) {
          try {
            const parsed = JSON.parse(savedData);
            if (parsed.skills && parsed.skills.length > 0) {
              updateStep2({ skills: parsed.skills });
            }
          } catch (e) {
            // Ignore - if parsing fails, keep current state
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (
        !isEdit &&
        !isReopen &&
        !id &&
        (title || company_name || editorValue || file)
      ) {
        saveFormDataToLocalStorage();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [title, company_name, editorValue, file, isEdit, isReopen, id]);

  return (
    <DashboardLayout>
      <Navbar title="Add Job" description="View and Manage Jobs" />

      <div>
        <h3 className="text-sm flex items-center gap-1 text-gray-700">
          <HomeOutlined className="text-gray-500" />
          <span>Jobs&nbsp;</span>
          <RightOutlined className="text-xs" />
          <span>&nbsp;Add Job</span>
        </h3>
      </div>

      <div className="flex justify-center items-center mt-8">
        <div className="flex items-center">
          <div className="flex items-center justify-center w-6 h-6 rounded-full border border-gray-500 text-gray-500 text-sm font-semibold">
            1
          </div>
          <div className="w-60 h-1 bg-blue-950"></div>
          <div className="flex items-center justify-center w-6 h-6 rounded-full border border-gray-500 text-gray-500 text-sm font-semibold">
            <span className="cursor-not-allowed">2</span>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-md p-3 mt-6 w-full mx-auto recruiter-job-container">
        <div className="grid gap-4">
          <Spin spinning={isAutoFilling} tip="Analyzing job description...">
            <Form
              form={form}
              onFinish={handleSubmit}
              layout="vertical"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 mt-5"
            >
              <div className="col-span-4 mb-6">
                <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
                  <h3 className="text-lg font-semibold mb-4">
                    Job Description
                    {isAutoFilling && (
                      <span className="ml-3 text-sm text-blue-600">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Analyzing document...
                      </span>
                    )}
                  </h3>

                  <Form.Item
                    name="descriptionMethod"
                    label="How would you like to provide the job description?"
                    className="col-span-4"
                    data-tour="description-method"
                    tooltip="Upload a file or paste text to auto-fill the form fields below"
                    rules={[
                      {
                        required: true,
                        message:
                          "Please select how you want to provide the job description",
                        validator: (_, value) => {
                          if (!localDescriptionInputType) {
                            return Promise.reject("Please select an option");
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    validateTrigger={["onChange"]}
                  >
                    <Radio.Group
                      onChange={(e) => {
                        const v = e.target.value as "upload" | "manual";
                        const previousValue = localDescriptionInputType;

                        setLocalDescriptionInputType(v);
                        form.setFieldValue("descriptionMethod", v);

                        setEditorValue("");
                        setFile(null);
                        form.resetFields([
                          "title",
                          "company_name",
                          "location_country",
                          "location_city",
                          "level",
                          "type",
                          "employment_type",
                          "experience",
                          "language",
                          "sector_id",
                          "customTitle",
                          "manualDescription",
                        ]);

                        updateStep1({
                          title: undefined,
                          company_name: undefined,
                          location_country: undefined,
                          location_city: undefined,
                          level: undefined,
                          type: undefined,
                          employment_type: undefined,
                          experience: undefined,
                          language: undefined,
                          sector_id: undefined,
                          jobTitleId: undefined,
                        });

                        updateStep2({
                          description: "",
                          descriptionInputType: v,
                          descriptionFile: null,
                          skills: [],
                        });

                        // Reset local state
                        setSelectedCountry(null);
                        setLanguageState(null);
                        setSelectedJobTitleId(null);
                        setManualQuillKey((k) => k + 1);

                        // Clear stored data
                        clearStoredData();
                        localStorage.removeItem("jobCreationFormData");

                        form.validateFields(["descriptionMethod"]);
                      }}
                      value={localDescriptionInputType}
                    >
                      <Radio value="upload">Upload Description File</Radio>
                      <Radio value="manual">Paste Text</Radio>
                    </Radio.Group>
                  </Form.Item>

                  {localDescriptionInputType === "upload" && (
                    <Form.Item
                      label="Upload Job Description"
                      name="descriptionFile"
                      className="col-span-4"
                      rules={[
                        {
                          required: true,
                          message: "Please upload a job description file",
                          validator: () => {
                            if (!file && !description) {
                              return Promise.reject(
                                "Please upload a job description file",
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      validateTrigger={["onChange"]}
                    >
                      <div>
                        {file ? (
                          <div className="inline-block min-w-[420px] border border-gray-300 rounded-md px-4 py-3 bg-white">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3 min-w-0">
                                <i className="ri-upload-2-line text-gray-500 text-lg" />
                                <span className="truncate text-sm text-gray-700">
                                  {file.name}
                                </span>
                              </div>
                              <div className="flex items-center gap-4 pr-2">
                                <span className="flex items-center justify-center bg-green-500 text-white rounded-full w-5 h-5">
                                  <i className="ri-check-line text-xs" />
                                </span>

                                <button
                                  type="button"
                                  onClick={() => {
                                    if (resetTimeoutRef.current) {
                                      clearTimeout(resetTimeoutRef.current);
                                    }

                                    setFile(null);

                                    setUploadKey((prev) => prev + 1);

                                    form.resetFields([
                                      "descriptionFile",
                                      "title",
                                      "company_name",
                                      "location_country",
                                      "location_city",
                                      "level",
                                      "type",
                                      "employment_type",
                                      "experience",
                                      "language",
                                      "sector_id",
                                      "customTitle",
                                    ]);

                                    setEditorValue("");
                                    setDescriptionExceedsManualLimit(false);
                                    setAutoFillCount(0);

                                    updateStep1({
                                      title: undefined,
                                      company_name: undefined,
                                      location_country: undefined,
                                      location_city: undefined,
                                      level: undefined,
                                      type: undefined,
                                      employment_type: undefined,
                                      experience: undefined,
                                      language: undefined,
                                      sector_id: undefined,
                                      jobTitleId: undefined,
                                    });

                                    updateStep2({
                                      description: "",
                                      descriptionFile: null,
                                      descriptionInputType: "upload",
                                      skills: [],
                                    });

                                    setSelectedJobTitleId(null);
                                    setSelectedCountry(null);
                                    setLanguageState(null);

                                    clearStoredData();
                                  }}
                                  className="text-gray-500 hover:text-red-500"
                                >
                                  <i className="ri-delete-bin-line text-lg" />
                                </button>
                              </div>
                            </div>
                            <div className="mt-3">
                              <div className="h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-blue-600 transition-all duration-300"
                                  style={{ width: "100%" }}
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <Upload
                            key={uploadKey}
                            accept=".txt, .docx, .pdf"
                            showUploadList={false}
                            customRequest={(options) => {
                              options.onSuccess &&
                                options.onSuccess({}, options.file);
                            }}
                            onChange={handleFileChange}
                          >
                            <div className="max-w-xl min-w-[420px]">
                              <div className="flex items-center border border-gray-300 rounded-md overflow-hidden cursor-pointer bg-white hover:border-blue-500 transition-colors">
                                <span className="bg-gray-100 px-4 py-2 text-sm text-gray-700 border-r border-gray-300">
                                  Choose file
                                </span>
                                <span className="px-3 py-2 text-sm text-gray-500 truncate flex-1">
                                  Upload .txt, .docx, or .pdf
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-2">
                                <i className="ri-information-line mr-1"></i>
                                Supported formats: PDF, DOCX, TXT. File content
                                will be automatically processed to fill form
                                fields.
                              </p>
                            </div>
                          </Upload>
                        )}
                      </div>
                    </Form.Item>
                  )}

                  {localDescriptionInputType === "manual" && (
                    <div className="col-span-4">
                      <Form.Item
                        label="Write or paste Job Description"
                        name="manualDescription"
                        className="mb-0"
                        rules={[
                          {
                            required: true,
                            message: "Please enter a job description",
                            validator: (_, value) => {
                              if (
                                !value ||
                                value.replace(/<(.|\n)*?>/g, "").trim()
                                  .length === 0
                              ) {
                                return Promise.reject(
                                  "Please enter a job description",
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <div>
                          <ReactQuill
                            key={manualQuillKey}
                            theme="snow"
                            value={editorValue}
                            onChange={(val) => {
                              setEditorValue(val);
                              updateStep2({ description: val });
                              form.setFieldsValue({ manualDescription: val });
                            }}
                            modules={{
                              toolbar: [
                                [{ header: [1, 2, false] }],
                                [{ bold: true }, { italic: true }],
                                [{ list: "ordered" }, { list: "bullet" }],
                                [{ link: true }],
                                ["clean"],
                              ],
                            }}
                          />
                          <div className="mt-3 flex justify-end">
                            <Button
                              type="primary"
                              size="middle"
                              icon={
                                isAutoFilling ? (
                                  <Spin size="small" />
                                ) : (
                                  <RobotOutlined />
                                )
                              }
                              onClick={handleExtractManualText}
                              disabled={
                                !editorValue ||
                                editorValue.replace(/<(.|\n)*?>/g, "").trim()
                                  .length === 0 ||
                                isAutoFilling
                              }
                              className="flex items-center gap-2 px-4 py-1"
                            >
                              {isAutoFilling ? "Extracting..." : "Extract"}
                            </Button>
                          </div>
                        </div>
                      </Form.Item>
                    </div>
                  )}
                </div>
              </div>

              <Form.Item
                rules={generalValidationRules}
                name="title"
                data-tour="job-title"
                label="Job Title"
                className="md:col-span-2"
                tooltip="This can be auto-filled from your job description"
              >
                <Select
                  options={jobTitleOptions}
                  showSearch
                  placeholder="Select or auto-filled from description"
                  allowClear
                  loading={loadingJobTitles || isAutoFilling}
                  filterOption={(input, option) => {
                    if (option?.value === "others") return true;
                    return (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }}
                  onChange={handleJobTitleChange}
                  className="h-9 mb-3"
                  disabled={titleLocked}
                />
              </Form.Item>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 col-span-4 gap-y-3 md:gap-y-0 gap-x-3 -mt-3">
                <Form.Item
                  shouldUpdate={(prev, cur) => prev.title !== cur.title}
                  noStyle
                >
                  {({ getFieldValue }) => {
                    const selectedTitle = getFieldValue("title");
                    return selectedTitle === "others" ? (
                      <Form.Item
                        name="customTitle"
                        label="Custom Job Title"
                        rules={[
                          {
                            required: true,
                            message: "Please enter a custom job title",
                          },
                          {
                            validator: (_, value) => {
                              if (!value) return Promise.resolve();
                              const exists = jobTitleOptions.some(
                                (job: { value: string }) =>
                                  job.value.toLowerCase() ===
                                  value.toLowerCase(),
                              );
                              if (exists) {
                                return Promise.reject(
                                  "This job title already exists. Please choose a different one.",
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                        className="md:col-span-2"
                      >
                        <Input
                          placeholder="Enter your job title"
                          disabled={titleLocked}
                          onChange={(e) =>
                            updateStep1({ title: e.target.value })
                          }
                        />
                      </Form.Item>
                    ) : null;
                  }}
                </Form.Item>

                <Form.Item
                  rules={generalValidationRules}
                  name="company_name"
                  data-tour="company-name"
                  label="Company Name"
                  tooltip="Auto-filled from description if available"
                >
                  <Input
                    onChange={(e) =>
                      updateStep1({ company_name: e.target.value })
                    }
                    disabled={isEdit}
                    placeholder="e.g., Acme Inc."
                  />
                </Form.Item>

                <Form.Item
                  rules={generalValidationRules}
                  name="sector_id"
                  label="Sector"
                  data-tour="sector"
                >
                  <Select
                    allowClear
                    showSearch
                    placeholder="Select sector"
                    loading={loadingSector}
                    optionFilterProp="label"
                    options={sectorOptions}
                    onChange={(value) => updateStep1({ sector_id: value })}
                  />
                </Form.Item>

                <Form.Item
                  rules={generalValidationRules}
                  name="location_country"
                  data-tour="country"
                  label="Country"
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="Select country"
                    options={
                      COUNTRIES?.length
                        ? COUNTRIES.map((country) => ({
                          value: country.name,
                          label: country.name,
                        }))
                        : []
                    }
                    onChange={handleCountryChange}
                  />
                </Form.Item>

                <Form.Item
                  shouldUpdate={(prevValues, curValues) =>
                    prevValues.location_country !== curValues.location_country
                  }
                  noStyle
                >
                  {({ getFieldValue }) => (
                    <Form.Item
                      rules={generalValidationRules}
                      name="location_city"
                      label="Location (City)"
                      data-tour="city"
                    >
                      <Select
                        showSearch
                        options={cityOptions}
                        allowClear
                        placeholder="Select city"
                        disabled={!getFieldValue("location_country")}
                        loading={loadingCities}
                        onChange={(value) =>
                          updateStep1({ location_city: value })
                        }
                      />
                    </Form.Item>
                  )}
                </Form.Item>

                <Form.Item
                  rules={generalValidationRules}
                  data-tour="experience-level"
                  name="level"
                  label="Experience Level"
                  tooltip="Auto-filled from description if available"
                >
                  <Select
                    options={LEVEL_LIST}
                    placeholder="Select level"
                    allowClear
                    onChange={(value) => updateStep1({ level: value })}
                  />
                </Form.Item>

                <Form.Item
                  rules={generalValidationRules}
                  name="type"
                  data-tour="work-model"
                  label="Work Model"
                  tooltip="Auto-filled from description if available"
                >
                  <Select
                    options={WORK_MODELS}
                    placeholder="Select work model"
                    allowClear
                    onChange={(value) => updateStep1({ type: value })}
                  />
                </Form.Item>

                <Form.Item
                  rules={generalValidationRules}
                  data-tour="employment-type"
                  name="employment_type"
                  label="Employment Type"
                  tooltip="Auto-filled from description if available"
                >
                  <Select
                    options={EMPLOYMENT_TYPES}
                    placeholder="Select employment type"
                    allowClear
                    onChange={(value) =>
                      updateStep1({ employment_type: value })
                    }
                  />
                </Form.Item>

                <Form.Item
                  rules={generalValidationRules}
                  name="experience"
                  data-tour="years-of-experience"
                  label="Years of Experience"
                  tooltip="Auto-filled from description if available (e.g., '3-5' or '5+ years')"
                  normalize={(v) =>
                    typeof v === "string"
                      ? v.replace(/[^0-9\-+ ]/g, "")
                      : (v ?? "")
                  }
                >
                  <Input
                    className="w-full"
                    placeholder="e.g., 3 - 5"
                    onChange={(e) =>
                      updateStep1({
                        experience: e.target.value.replace(/[^0-9\-+ ]/g, ""),
                      })
                    }
                  />
                </Form.Item>

                <Form.Item
                  name="language"
                  label="Preferred Language"
                  rules={[
                    {
                      required: true,
                      message: "Please select a preferred language",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    options={LANGUAGES}
                    placeholder="Select preferred language"
                    onChange={handleLanguageChange}
                    allowClear
                  />
                </Form.Item>

                <Form.Item
                  name="expiryDate"
                  label="Job Post Expiry Date"
                  data-tour="expiry-date"
                  rules={generalValidationRules}
                >
                  <DatePicker
                    disabledDate={disablePastDates}
                    className="w-full"
                    format="DD/MM/YYYY"
                    onChange={(date) => updateStep1({ expiryDate: date })}
                    placeholder="Select expiry date"
                  />
                </Form.Item>
              </div>
            </Form>
          </Spin>
        </div>

        <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200">
          <Button onClick={handleCancel} className="px-8">
            Cancel
          </Button>
          <div>
            <AppButton
              label="Next"
              type="submit"
              isLoading={false}
              disabled={isAutoFilling}
              handleClick={() => form.submit()}
            />
          </div>
        </div>
      </div>
      <p className="mb-10">
        <br />
        <br />
        <br />
      </p>
    </DashboardLayout >
  );
};
